import React, { forwardRef } from 'react';

export interface TextareaProps extends React.TextareaHTMLAttributes<HTMLTextAreaElement> {
  label?: string;
  helperText?: string;
  error?: string;
  textareaSize?: 'sm' | 'md' | 'lg';
}

export const Textarea = forwardRef<HTMLTextAreaElement, TextareaProps>(
  (
    {
      label,
      helperText,
      error,
      textareaSize = 'md',
      className = '',
      id,
      required,
      disabled,
      ...props
    },
    ref
  ) => {
    const textareaId = id || `textarea-${Math.random().toString(36).substr(2, 9)}`;
    const errorId = `${textareaId}-error`;
    const helperTextId = `${textareaId}-helper`;

    // Size-based padding — matches Input's compact scale
    const sizeClasses = {
      sm: 'px-3 py-2 text-xs',
      md: 'px-3.5 py-2.5 text-sm',
      lg: 'px-4 py-3 text-sm',
    };

    // Base textarea classes — matches the site's flat, sharp-cornered form language
    const baseTextareaClasses = `
      w-full
      border
      bg-white
      text-[#404040]
      placeholder-[#A3A3A3]
      transition-colors duration-200
      focus:outline-none
      focus:border-[#E85C27]
      disabled:bg-[#F0EDE8]
      disabled:text-[#A3A3A3]
      disabled:cursor-not-allowed
      resize-vertical
      ${error ? 'border-red-400 focus:border-red-400' : 'border-[#E8E6E2]'}
      ${sizeClasses[textareaSize]}
      ${className}
    `.trim().replace(/\s+/g, ' ');

    return (
      <div className="w-full">
        {label && (
          <label
            htmlFor={textareaId}
            className="block text-xs uppercase tracking-wider text-[#A3A3A3] mb-1.5"
          >
            {label}
            {required && <span className="text-red-400 ml-1">*</span>}
          </label>
        )}

        <textarea
          ref={ref}
          id={textareaId}
          className={baseTextareaClasses}
          disabled={disabled}
          required={required}
          aria-invalid={!!error}
          aria-describedby={
            error ? errorId : helperText ? helperTextId : undefined
          }
          {...props}
        />

        {error && (
          <p id={errorId} className="text-xs text-red-500 mt-1">
            {error}
          </p>
        )}

        {helperText && !error && (
          <p id={helperTextId} className="text-xs text-[#6B6B6B] mt-1">
            {helperText}
          </p>
        )}
      </div>
    );
  }
);

Textarea.displayName = 'Textarea';
